import { useRef } from 'react';

const ALPHABET = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#'];

// ─── FiltersAlphabeticalIndex ─────────────────────────────────────────────────

export interface FiltersAlphabeticalIndexProps {
  presentLetters: Set<string>;
  onLetterClick: (letter: string) => void;
}

export function FiltersAlphabeticalIndex({ presentLetters, onLetterClick }: FiltersAlphabeticalIndexProps) {
  return (
    <div className="fixed top-0 right-0 z-10 flex h-screen w-10.5 shrink-0 flex-col items-center justify-center bg-white py-6">
      {ALPHABET.map(letter => (
        <button
          key={letter}
          type="button"
          aria-label={`Jump to ${letter}`}
          disabled={!presentLetters.has(letter)}
          className="font-pp-neue-montreal text-jncquoi-neutral-800 w-2.5 text-center text-[12px] font-semibold tracking-[0.5px] disabled:opacity-40"
          onClick={() => onLetterClick(letter)}
        >
          {letter}
        </button>
      ))}
    </div>
  );
}

// ─── FiltersAlphabetical ──────────────────────────────────────────────────────

export interface FiltersAlphabeticalProps<T> {
  items: T[];
  /** Extract the string used to determine the item's alphabetical group. */
  getLabel: (item: T) => string;
  /** Render function called once per letter group with the items belonging to that letter. */
  children: (items: T[], letter: string) => React.ReactNode;
  /** Pixel offset subtracted from the element's top before scrolling — use to compensate for a fixed header. */
  scrollOffset?: number;
}

export function FiltersAlphabetical<T>({
  items,
  getLabel,
  children,
  scrollOffset,
}: FiltersAlphabeticalProps<T>) {
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const groups: Record<string, T[]> = {};
  for (const item of items) {
    const first = getLabel(item).charAt(0).toUpperCase();
    const key = /^[A-Z]$/.test(first) ? first : '#';
    (groups[key] ??= []).push(item);
  }

  const presentLetters = new Set(Object.keys(groups));

  function scrollToLetter(letter: string) {
    const el = sectionRefs.current[letter];
    if (!el) return;
    if (scrollOffset !== undefined) {
      const top = el.getBoundingClientRect().top + window.scrollY - scrollOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  return (
    <div>
      <FiltersAlphabeticalIndex presentLetters={presentLetters} onLetterClick={scrollToLetter} />
      <div className="flex flex-col gap-8">
        {ALPHABET.filter(l => presentLetters.has(l)).map(letter => (
          <div
            key={letter}
            ref={el => {
              sectionRefs.current[letter] = el;
            }}
            className="flex flex-col"
          >
            <div className="flex items-center py-1">
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-semibold tracking-[0.5px]">
                {letter}
              </span>
            </div>
            {children(groups[letter], letter)}
          </div>
        ))}
      </div>
    </div>
  );
}
